/**
 * The client detail — KANON `Detal klienta v3`, variant **a**, and the host of all six tabs.
 *
 * ── v3's shell in one sentence ────────────────────────────────────────────────────────────────
 * Two fixed bars over one scrolling body. The top bar carries WHO this is — breadcrumb, avatar,
 * name, status, how long they have been a client, and the pair of arrows that walk the trainer's
 * list — so the context never scrolls away. v3 repeats the tab's own name up here as well and this
 * app does not: the owner took it off on 1 September 2026 ("nie duplikuj słowa kalendarz"), and the
 * departure is recorded at the markup and in `AGENTS.md`. The bar under it carries the six tabs and
 * one line about the tab in view. Under both, the body: a summary column ON PRZEGLĄD ONLY, and the
 * tab's own content beside it.
 *
 * ── The summary column stands on Przegląd and nowhere else ────────────────────────────────────
 * Owner, 31 August 2026 (v3's own note: "Kolumna podsumowania siedzi tylko na Przeglądzie, reszta
 * zakładek dostaje pełną szerokość"). This REPLACES the v2 rule that the column stood on every tab
 * but Kalendarz, and it absorbs the Kalendarz exception rather than keeping it as a special case:
 * every tab except Przegląd is now full width, so the week grid gets the same 1448px it was given
 * a rule of its own to get. The identity the column used to carry moved into the top bar, which is
 * what makes the column optional at all — losing it no longer loses the client's name.
 *
 * ── `renderCalendar` is a LAYERING seam, not a variation point ─────────────────────────────────
 * The Kalendarz tab is the existing `pages/assigned-plan-calendar` screen, mounted whole. FSD
 * forbids a page importing another page, so composition happens in the route — `app/` may import
 * from every layer — and this screen takes the calendar as a node. One adapter is normally a
 * hypothetical seam and this file would refuse it; it is taken because the alternative is a banned
 * import today, not a second variation later. `sessionOverlay` is a second node for the same
 * reason.
 *
 * ── Only the visible tab is mounted ───────────────────────────────────────────────────────────
 * Parity with mobile, and the reason each tab's reads live in its own component: an unmounted tab
 * has no query, so switching to Rekordy is the only thing that fetches records. There is no
 * exception left. The summary column's five reads were held here until 2 September 2026, which
 * fired four of them on the five tabs that draw no column at all; they moved into
 * `useSummaryColumn` with the column itself, so "only what is on screen fetches" is now true by
 * construction rather than by a flag.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { AssignSurveyDialog } from '~/features/assign-survey';
import { ClipPlayer } from '~/features/clip-player';
import { CreatePlanDialog } from '~/features/create-plan';
import { EndCoachingMenu, type EndCoachingMenuItem } from '~/features/end-coaching';

import { newConversationKey, useBuddyEntry } from '~/entities/ai-conversation';
import { planCalendarLink, rosterContextSearch, type RosterContext } from '~/entities/client';
import { useSessionUser } from '~/entities/session';
import { screenMotion, useScreenMotionPreference } from '~/shared/design-system/motion';
import { type SessionOpenIntent } from '~/shared/lib/sessionOpener';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import { useSessionOpenerReturn } from '~/shared/lib/useSessionOpenerReturn';
import { HintPopover } from '~/shared/ui/HintPopover';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import { TabStrip, tabButtonId, tabPanelId } from '~/shared/ui/TabStrip';
import { preferenceStore } from '~/shared/lib/preferenceStore';
import { PearlMark } from '~/shared/ui/PearlMark';
import ember from '~/shared/ui/emberButton.module.css';
import ghost from '~/shared/ui/ghostButton.module.css';

import { clientSurveysQueryKey } from '../api/clientDetailQueries';
import { cardMenuEntries, type CardMenuEntry } from '../model/cardMenu';
import {
  clientDetailTabLabel,
  DEFAULT_CLIENT_DETAIL_TAB,
  visibleClientDetailTabs,
  type ClientDetailTab,
} from '../model/detailTabs';
import { type ClientNeighbour } from '../model/clientNeighbours';
import { useClientDetail } from '../lib/useClientDetail';
import { useClientNeighbours } from '../lib/useClientNeighbours';
import { useClientNoteEdit } from '../lib/useClientNoteEdit';
import { useFormaUnread, useRefreshTrainedExercises } from '../lib/useForma';
import { useNutritionTabStands } from '../lib/useNutrition';
import { BodyTab } from './BodyTab';
import { FormaTab } from './FormaTab';
import { NutritionTab } from './NutritionTab';
import { OverviewTab } from './OverviewTab';
import { RecordsTab } from './RecordsTab';
import { SelfBookingDialog } from './SelfBookingDialog';
import { SurveysTab } from './SurveysTab';
import { TrendsTab } from './TrendsTab';
import { SummaryColumn } from './SummaryColumn';
import { CardAnnouncerProvider } from './cardAnnouncer';
import { TabMetaProvider } from './detailTabMeta';
import photoBox from '~/shared/ui/photoBox.module.css';

import styles from './ClientDetailScreen.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

interface ClientDetailScreenProps {
  /** `null` where the route parameter was not a usable id — the screen says so and stops. */
  readonly clientId: number | null;
  readonly tab: ClientDetailTab;
  readonly onSelectTab: (tab: ClientDetailTab) => void;
  readonly onBack: () => void;
  /**
   * The list the address describes — the roster's search, tab and ordering, as the URL carried
   * them. It is what `‹ Poprzednia / Następna ›` walks; see `model/clientNeighbours`.
   */
  readonly listContext: RosterContext;
  /** Open another client, keeping the list context and the tab. */
  readonly onOpenClient: (clientId: number) => void;
  /**
   * Open one of this client's sessions. On Przegląd that is the overlay over this screen; on
   * Kalendarz it is the calendar's own panel, which has a plan slot and can draw a prescription.
   */
  readonly onOpenSession: (workoutExecutionId: number, intent: SessionOpenIntent) => void;
  /** The plan calendar, composed by the route. See the header. */
  readonly renderCalendar: () => ReactNode;
  /**
   * The session panel laid over this screen — or `null` when none is open. `openerKey` travels WITH
   * the node because this screen needs both and they may not disagree: it names the control the
   * overlay was opened from, which is where focus goes back to when it closes.
   */
  readonly sessionOverlay: { readonly openerKey: string; readonly node: ReactNode } | null;
  /** The clip the full-screen player shows, from `?clip=`, or `null`. */
  readonly clipId: number | null;
  /**
   * Put a clip in the address, or take it out. `push` opens the player so Back closes it;
   * `replace` switches clips inside it. `null` closes it, and the route decides where that lands —
   * the screen the player was opened from.
   */
  readonly onShowClip: (clipId: number | null, history: 'push' | 'replace') => void;
  /** The survey occurrence Ankiety opens on, from `?survey=`, or `null` for its own default. */
  readonly surveyId: number | null;
  /** Open Ankiety on one occurrence — the Przegląd survey task's „Przejrzyj". */
  readonly onOpenSurvey: (surveyAssignmentId: number) => void;
}

/**
 * **A screen with nothing behind it**, and the one thing the trainer can do about it.
 *
 * Three of these — the route carried no client id, the client does not exist or is not this
 * trainer's, and the header read failed transiently — and they were three copies of five lines,
 * two of them identical down to the keys. What the third one differs in is exactly what should
 * differ: the words, and whether the control goes back or tries again.
 *
 * It carries an `<h1>`, deliberately. `AppShell` moves focus to the heading after a route change,
 * and a screen that renders none is one the shell silently fails to announce — so a dead end has to
 * be as announceable as a loaded screen.
 */
function DeadEnd({
  title,
  body,
  actionLabel,
  onAction,
}: {
  title: string;
  body: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <main className={styles.deadEnd}>
      <h1 className={styles.deadEndTitle}>{title}</h1>
      <p className={styles.deadEndBody}>{body}</p>
      <button type="button" className={styles.deadEndLink} onClick={onAction}>
        {actionLabel}
      </button>
    </main>
  );
}

export function ClientDetailScreen({
  clientId,
  tab,
  onSelectTab,
  onBack,
  listContext,
  onOpenClient,
  onOpenSession,
  renderCalendar,
  sessionOverlay,
  clipId,
  onShowClip,
  surveyId,
  onOpenSurvey,
}: ClientDetailScreenProps) {
  const { t } = useTranslation();

  if (clientId === null) {
    return (
      <DeadEnd
        title={t('clients.detail.states.notFoundTitle')}
        body={t('clients.detail.states.notFoundMessage')}
        actionLabel={t('web:clientDetail.breadcrumbRoster')}
        onAction={onBack}
      />
    );
  }

  return (
    <ClientDetail
      clientId={clientId}
      tab={tab}
      onSelectTab={onSelectTab}
      onBack={onBack}
      listContext={listContext}
      onOpenClient={onOpenClient}
      onOpenSession={onOpenSession}
      renderCalendar={renderCalendar}
      sessionOverlay={sessionOverlay}
      clipId={clipId}
      onShowClip={onShowClip}
      surveyId={surveyId}
      onOpenSurvey={onOpenSurvey}
    />
  );
}

/**
 * Split from the guard above because the hooks below may not run behind a conditional return. The
 * two are one component; the boundary is React's rule, not a decision about structure.
 */
function ClientDetail({
  clientId,
  tab: requestedTab,
  onSelectTab,
  onBack,
  listContext,
  onOpenClient,
  onOpenSession,
  renderCalendar,
  sessionOverlay,
  clipId,
  onShowClip,
  surveyId,
  onOpenSurvey,
}: ClientDetailScreenProps & { clientId: number }) {
  const navigate = useNavigate();
  const sessionUser = useSessionUser();
  const userId = sessionUser?.id ?? null;
  // Odżywianie stands where R16 opens it; a link to it opened by anyone else lands on the default
  // tab rather than on a tab the strip does not show. While the probe is out, a link to it holds
  // the tab (its own skeleton) instead of flashing Przegląd and jumping.
  const nutritionStands = useNutritionTabStands(clientId, sessionUser?.nutritionEnabled ?? false);
  const visibleTabs = visibleClientDetailTabs(
    nutritionStands === true || (nutritionStands === 'pending' && requestedTab === 'nutrition'),
  );
  const tab = visibleTabs.includes(requestedTab) ? requestedTab : DEFAULT_CLIENT_DETAIL_TAB;
  const buddy = useBuddyEntry(userId);
  const { t } = useTranslation();
  const reducedMotion = useScreenMotionPreference();
  const detail = useClientDetail(clientId);
  const neighbours = useClientNeighbours(clientId, listContext);
  const note = useClientNoteEdit(clientId);
  const formaUnread = useFormaUnread(clientId);
  const refreshTrainedExercises = useRefreshTrainedExercises(clientId);
  // Where the Forma panel's small player was left, for the full player it opened. Not in the
  // address: a reload or a shared link opens a film at the top.
  const [clipStart, setClipStart] = useState<{ readonly clipId: number; readonly seconds: number } | null>(
    null,
  );

  /**
   * „Przypisz plan" and the dialog it opens — „Nowy plan", built FOR this client (23 September 2026).
   *
   * Two refs, because the button is CONDITIONAL: it is drawn only while this client has no plan.
   * Base UI resolves `finalFocus` at close time, so the callback hands back the button when it
   * survived and the identity bar's `<h1>` when it did not.
   */
  const assignButton = useRef<HTMLButtonElement>(null);
  const clientHeading = useRef<HTMLHeadingElement>(null);
  const [assigning, setAssigning] = useState(false);
  /** The `⋯` menu's „Ustawienia rozliczeń" dialog. */
  const [settingsOpen, setSettingsOpen] = useState(false);
  /**
   * „Przypisz ankietę" (8 October 2026), from the `⋯` menu or the Ankiety tab. `surveyOpener` is the
   * tab's control that opened it, or `null` from the menu — where Base UI already returns focus to
   * the `⋯`. The tab's empty-state control is gone once the assignment's first occurrence lands, so
   * focus falls back to the identity bar's `<h1>` then.
   */
  const [surveyAssigning, setSurveyAssigning] = useState(false);
  const surveyOpener = useRef<HTMLElement | null>(null);
  const openAssignSurvey = (opener: HTMLElement | null) => {
    surveyOpener.current = opener;
    setSurveyAssigning(true);
  };
  const announcer = useAnnouncer();
  const { announce } = announcer;
  // A sentence about the tab the trainer just left is not said again over the next one.
  useEffect(() => {
    announce('');
  }, [announce, tab]);

  /** The tab bar's right-hand line, published by whichever tab is mounted. See `detailTabMeta`. */
  const [tabMeta, setTabMeta] = useState<string | null>(null);
  const publishTabMeta = useCallback((text: string | null) => {
    setTabMeta(text);
  }, []);

  /**
   * The summary column stands on Przegląd alone, so leaving Przegląd takes it off the screen — and
   * the goal card's tab links are ways of leaving it. A focused
   * element that leaves the document drops focus to `<body>`, so the next Tab restarts at the top
   * of the page. The stop is handed to the tab that was just chosen: it is the control that now
   * represents where the trainer is, and the panel follows it in tab order.
   *
   * Set by the column's own handler rather than derived from `tab` changing, so a tab opened from
   * the address bar, the roster or a link does not have focus yanked out from under it. A ref
   * rather than state: a one-shot errand between an event and the commit that follows it.
   */
  const handOffFocusToTab = useRef(false);
  useEffect(() => {
    if (!handOffFocusToTab.current) return;
    handOffFocusToTab.current = false;
    document.getElementById(tabButtonId(tab))?.focus();
  }, [tab]);

  // Where focus goes when the session overlay closes: back on the control that opened it. The key
  // is the PAIR of session and intent, because Przegląd has two controls for the same session.
  useSessionOpenerReturn(sessionOverlay?.openerKey ?? null);

  if (detail.notFound) {
    return (
      <DeadEnd
        title={t('clients.detail.states.notFoundTitle')}
        body={t('clients.detail.states.notFoundMessage')}
        actionLabel={t('web:clientDetail.breadcrumbRoster')}
        onAction={onBack}
      />
    );
  }

  if (detail.failed) {
    return (
      <DeadEnd
        title={t('clients.detail.states.errorTitle')}
        body={t('clients.detail.states.errorMessage')}
        actionLabel={t('web:clientDetail.states.retry')}
        onAction={detail.refetch}
      />
    );
  }

  const identity = detail.identity;
  const clientName = identity?.name ?? t('web:clientDetail.states.loading');

  /**
   * The plan card is one link into this plan's cycle grid — owner, 2 September 2026, "po prostu po
   * kliknięciu na całą kartę ma tam przenosić". Assembled here rather than in the column because
   * both halves of it belong to the screen: the address carries the roster context the trainer
   * arrived on, exactly like every other navigation off this screen, and the accessible name
   * carries the client whose plan it is.
   */
  const planRoute = planCalendarLink({
    clientId,
    assignmentId: detail.plan?.assignmentId,
    list: rosterContextSearch(listContext),
  });
  const planCardLink =
    planRoute === null || detail.plan === null
      ? null
      : {
          route: planRoute,
          label: t('web:clientDetail.plan.openCalendar', {
            plan: detail.plan.name,
            name: clientName,
          }),
        };
  /** v3: the summary column is Przegląd's. Every other tab gets the whole zone. */
  const summaryColumnStands = tab === 'overview';

  /** The `⋯` menu's entries above „Zakończ współpracę" — which ones, and their order, are `model/cardMenu`. */
  const menuEntry: Record<CardMenuEntry, EndCoachingMenuItem> = {
    assignSurvey: {
      id: 'assignSurvey',
      label: t('web:clientDetail.surveys.assign'),
      onSelect: () => {
        openAssignSurvey(null);
      },
    },
    selfBooking: {
      id: 'selfBooking',
      label: t('web:clientDetail.selfBooking.menuItem'),
      onSelect: () => {
        setSettingsOpen(true);
      },
    },
  };
  const menuItems = cardMenuEntries(identity !== null).map((entry) => menuEntry[entry]);

  return (
    <main className={styles.page}>
      {/* ── the identity bar ────────────────────────────────────────────────────────────────
        * v3 puts the breadcrumb, the client and the tab's name on one 52px row, so no tab can
        * scroll the answer to "whose screen is this" off the top. It carries the document's only
        * `<h1>`, which is why the summary column can be absent on five tabs out of six without
        * leaving the page unnamed. */}
      <div className={styles.topBar}>
        <nav className={styles.crumbs} aria-label={t('web:clientDetail.breadcrumbRoster')}>
          <button type="button" className={styles.crumbLink} onClick={onBack}>
            {t('web:clientDetail.breadcrumbRoster')}
          </button>
          <span className={styles.crumbSep} aria-hidden="true">
            /
          </span>
        </nav>
        {/* ── The disc holds its slot before there is anything to put in it ──────────────────
          * The bar drew no avatar at all while the header read was open, so the client's name sat
          * 36px — the disc plus its gap — to the LEFT of where it was about to be and slid right
          * the moment the response landed. Measured on the production build, cold cache, with the
          * reads staggered: `<h1>` x 166.5 → 202.5 at 1469ms. That is the same rule every card on
          * this screen already follows, turned on its side.
          *
          * Empty rather than a spinner or a shimmer: it is the neutral ground the token already
          * gives it (`--bg-sunken` behind a hairline), which is what an avatar that has not
          * arrived looks like — and it is what the `<img>` branch shows too, since the photo is
          * painted over that same ground and the box never changes size whether the file arrives
          * late, arrives never, or is not there at all (measured 2 September 2026). */}
        {identity === null ? (
          <span className={styles.avatar} aria-hidden="true" />
        ) : identity.avatarUrl === null ? (
          <span className={styles.avatar} aria-hidden="true">
            {identity.initials}
          </span>
        ) : (
          <img
            className={`${String(photoBox.photo)} ${String(styles.avatarPhoto)}`}
            src={identity.avatarUrl}
            alt=""
            // Above the fold on every tab, and 28px: there is nothing to defer.
            decoding="async"
          />
        )}
        {/* `tabIndex={-1}` stated rather than left to `AppShell`, which sets it imperatively on
          * every route change: „Przypisz plan"'s focus return falls back here, and a heading that
          * cannot take focus fails that silently. */}
        <h1 ref={clientHeading} className={styles.clientName} tabIndex={-1}>
          {clientName}
        </h1>
        {identity === null ? null : (
          <span className={styles.statusPill}>
            {/* The dot repeats the status word for the eye and never carries it alone. */}
            <i
              className={`${styles.statusDot} ${identity.active ? styles.statusDotActive : ''}`}
              aria-hidden="true"
            />
            {identity.statusLabel}
          </span>
        )}
        {/* ── An invited client, opened before they answered (R2) ────────────────────────────────
          * The mobile app draws a band over Przegląd saying so, because every empty section below
          * reads the same for a client who stopped training and one who never opened the app, and
          * because the work prepared here is deleted if the offer lapses. Here it is the ⓘ beside
          * the status word, on every tab rather than on one, and the mobile app's own three
          * sentences behind it.
          *
          * Not a band, and that is the loading policy rather than taste: whether this client is
          * invited is known only once the header read answers, and a band arriving then pushes the
          * column and the tab down under the trainer — there is no box to reserve for a fact the
          * screen does not know yet. The status pill arrives with the same answer on a row whose
          * height is fixed, so the ⓘ beside it moves nothing. `active` is false exactly for the
          * contract's `Invited`. */}
        {identity !== null && !identity.active && (
          <HintPopover label={t('clients.detail.pendingOffer.title')}>
            {`${t('clients.detail.pendingOffer.body')} ${t('clients.detail.pendingOffer.warning')}`}
          </HintPopover>
        )}
        {/* v3 repeats the tab's own name here, 40px above the selected tab in the strip below.
          * REMOVED — owner, 1 September 2026: "nie duplikuj słowa kalendarz". The strip already
          * says where the trainer is, and the bar keeps what only it carries: the breadcrumb, the
          * avatar, the name and the status. `AGENTS.md` records the departure. */}
        {identity?.sinceLabel === undefined ? null : (
          <span className={styles.barSince}>{identity.sinceLabel}</span>
        )}
        {/* ── walking the trainer's list ─────────────────────────────────────────────────────
          * v3's `‹ Poprzednia / Następna ›`, built 1 September 2026. It walks the list the ADDRESS
          * describes rather than one this screen invented: the roster's search, tab and ordering
          * ride in the URL (`entities/client`'s `rosterContext`) and the neighbours are read off
          * the same `rosterView` the roster's own table renders. Both ends refuse rather than
          * wrap, and a refused arrow keeps its place in the tab order so the reason attached to it
          * stays reachable.
          *
          * The ⋯ menu v3 draws beside them IS built as of 16 September 2026, and it is one entry
          * rather than three. The note that used to stand here said it was deliberately absent
          * "because every entry it would carry is on the roster row's own menu"; the owner emptied
          * that menu the same day, so the reason expired and the act came here — which is where
          * the canon drew it in the first place. `features/end-coaching` owns it. */}
        <nav className={styles.walk} aria-label={t('web:clientDetail.nav.label')}>
          <WalkStep
            step={neighbours.previous}
            label={t('web:clientDetail.nav.previous')}
            reason={
              neighbours.onList
                ? t('web:clientDetail.nav.atStart')
                : t('web:clientDetail.nav.offList')
            }
            reasonId={`walk-previous-${String(clientId)}`}
            onOpen={onOpenClient}
          />
          <WalkStep
            step={neighbours.next}
            label={t('web:clientDetail.nav.next')}
            reason={
              neighbours.onList ? t('web:clientDetail.nav.atEnd') : t('web:clientDetail.nav.offList')
            }
            reasonId={`walk-next-${String(clientId)}`}
            onOpen={onOpenClient}
          />
        </nav>

        {/* Ending a coaching relationship. It leaves for the roster on success, because the client
          * this screen is describing is no longer one of the trainer's. */}
        <EndCoachingMenu
          customerId={clientId}
          clientName={clientName}
          onEnded={() => {
            void navigate({ to: '/klienci' });
          }}
          leadingItems={menuItems}
        />
        <SelfBookingDialog
          announce={announce}
          clientId={clientId}
          clientName={clientName}
          open={settingsOpen}
          onClose={() => {
            setSettingsOpen(false);
          }}
        />

        {/* ── „Przypisz plan", the assignment's SECOND entry point (21 September 2026) ────────
          * Owner-confirmed with variant D: the plan library is not where a trainer thinks about
          * one client. This is, and until now the only way in was the builder's top bar, which
          * means opening a program first and choosing the person afterwards.
          *
          * Three states, not a branch on the data: `identity` is null until the read answers, so
          * the button is drawn only once there IS an answer and that answer is "no plan". Gating
          * on `plan === null` alone would flash it in and back out on every load. It sits at the
          * END of the row, so its arrival moves nothing that was already there.
          *
          * It does not ask `useAssignGate`: there is no plan to judge until one is built. The
          * dialog starts one — from a template or from nothing — and the builder it opens is where
          * the plan is assigned. */}
        {/* „Plan z Buddym" (2 October 2026): a new conversation with Buddy about a plan FOR this
          * client. Offered for every client, with a running plan or without (owner, 3 October
          * 2026: the next plan is often drafted while the current one runs), once the detail has
          * answered, and only where Buddy is this trainer's: the standing is the one this device
          * remembers, as the navigation's entry reads it. While Buddy is
          * switched off or at its daily ceiling the module is still reachable and says why.
          * Neutral, since the row's one accent is the assignment. Each press starts a NEW
          * conversation (a key of its own); an earlier one for this client stays in the history. */}
        {identity !== null && buddy !== 'hidden' && userId !== null && (
          <button
            type="button"
            className={`${String(ghost.ghost)} ${String(styles.buddyPlan)}`}
            onClick={() => {
              newConversationKey(preferenceStore(), userId, clientId, () => crypto.randomUUID());
              void navigate({ to: '/buddy', search: { klient: clientId } });
            }}
          >
            <PearlMark state="still" className={styles.buddyPlanMark} />
            {t('web:clientDetail.plan.withBuddy')}
          </button>
        )}
        {identity !== null && detail.plan === null && (
          <button
            ref={assignButton}
            type="button"
            className={`${String(ember.ember)} ${String(styles.assignPlan)}`}
            onClick={() => {
              setAssigning(true);
            }}
          >
            {t('web:clientDetail.plan.assign')}
          </button>
        )}
      </div>

      {/* Mounted once the identity has answered, because the plan is built for a NAMED client: the
        * head echoes the name, and a copy is named after the first word of it. The button is
        * gated on the same answer, so nothing can open this before it exists. */}
      {identity !== null && (
        <CreatePlanDialog
          recipient={{ clientId, clientName: identity.name }}
          open={assigning}
          finalFocus={() => assignButton.current ?? clientHeading.current}
          onClose={() => {
            setAssigning(false);
          }}
        />
      )}

      {/* Mounted once the identity has answered, for the same reason as the plan's: the dialog names
        * the client, and the menu offers its entry on the same answer (`model/cardMenu`). „Utwórz
        * nową ankietę" opens the editor FOR this client; its first save assigns and comes back. */}
      {identity !== null && (
        <AssignSurveyDialog
          clientId={clientId}
          clientName={identity.name}
          open={surveyAssigning}
          announce={announce}
          invalidates={[clientSurveysQueryKey(clientId)]}
          finalFocus={() => {
            const opener = surveyOpener.current;
            return opener === null || opener.isConnected ? true : clientHeading.current;
          }}
          onClose={() => {
            setSurveyAssigning(false);
          }}
          onCreateNew={() => {
            setSurveyAssigning(false);
            void navigate({ to: '/raporty/nowa', search: { klient: clientId } });
          }}
        />
      )}

      {/* ── the tab bar ─────────────────────────────────────────────────────────────────────
        * A real tablist: one tab stop for the strip, arrows inside it. */}
      <TabStrip
        className={styles.tabBar}
        label={t('web:clientDetail.tabsLabel')}
        tabs={visibleTabs}
        selected={tab}
        renderLabel={(entry) =>
          entry === 'forma' && formaUnread ? (
            <span className={styles.tabWithDot}>
              {clientDetailTabLabel(entry, t)}
              <span aria-hidden="true" className={styles.tabDot} />
              <span className={hidden.srOnly}>{t('web:clientDetail.tabs.formaUnread')}</span>
            </span>
          ) : (
            clientDetailTabLabel(entry, t)
          )
        }
        onSelect={onSelectTab}
        trailing={tabMeta === null ? null : <span className={styles.tabMeta}>{tabMeta}</span>}
      />

      {/* INERT while a session or a clip is open. A scrim says the surface behind it is not to be
        * used, and `inert` is what makes that true for the keyboard and the accessibility tree as
        * well as for the pointer — without it, Tab walks straight out of the panel into a dimmed page
        * the trainer cannot see. It is the modality the overlay needs, without a focus trap and
        * without the panel having to claim a `role` that changes with its host. */}
      <div className={styles.body} inert={sessionOverlay !== null || clipId !== null}>
        {summaryColumnStands ? (
          <SummaryColumn
            clientId={clientId}
            onSaveNote={note.save}
            noteSaving={note.saving}
            noteFailed={note.failed}
            reducedMotion={reducedMotion}
            onOpenTab={(next) => {
              handOffFocusToTab.current = true;
              onSelectTab(next);
            }}
            planLink={planCardLink}
          />
        ) : null}

        {/* `tabIndex={0}` on the panel, on every tab rather than on the ones that need it.
          *
          * The tabs pattern requires it wherever a panel holds nothing focusable, and Ankiety is
          * exactly that. Applied unconditionally because the alternative is a rule that watches its
          * own children — and the panel is also the box these tabs scroll.
          *
          * The `key` is what replays the entrance: v3 alternates two identically-shaped keyframe
          * names so the browser restarts the animation on a node that did not change. Motion needs
          * no such trick — a new key is a new element, and the element is the tab's content, which
          * genuinely changed. */}
        {/* The card's ONE live region (`cardAnnouncer`): every tab and every dialog over the card
          * speaks through it. Outside the keyed panel, so it stands across a tab change rather
          * than being inserted together with the next tab's first sentence. */}
        <LiveRegionAnnouncer message={announcer.message} />
        <motion.div
          key={tab}
          className={styles.panel}
          role="tabpanel"
          id={tabPanelId(tab)}
          aria-labelledby={tabButtonId(tab)}
          tabIndex={0}
          {...screenMotion('enter', { reducedMotion })}
        >
          {/* A TABLE keyed by the tab, not a chain of ternaries.
            *
            * `CLIENT_DETAIL_TABS` is the declared source of truth for which tabs exist, and a
            * ternary chain is the one place on this screen that could disagree with it without a
            * type error: its last `else` silently meant `surveys`, so a seventh tab would have
            * rendered the survey list under its own name and nothing would have said so. A
            * `Record<ClientDetailTab, …>` makes that a compile error instead.
            *
            * `calendar` is a call rather than an element because it is the layering seam the header
            * describes — the screen is handed the other page's screen as a node. */}
          <CardAnnouncerProvider value={announce}>
            <TabMetaProvider value={publishTabMeta}>
              {(
                {
                  overview: () => (
                    <OverviewTab
                      clientId={clientId}
                      clientName={identity?.name ?? ''}
                      clientInitials={identity?.initials ?? ''}
                      onOpenSession={onOpenSession}
                      onAssignPlan={() => {
                        setAssigning(true);
                      }}
                      onOpenClip={(next) => {
                        onShowClip(next, 'push');
                      }}
                      onOpenSurvey={onOpenSurvey}
                      onOpenCalendar={() => {
                        // The band leaves with Przegląd: the stop goes to the tab it opened.
                        handOffFocusToTab.current = true;
                        onSelectTab('calendar');
                      }}
                      reducedMotion={reducedMotion}
                    />
                  ),
                  calendar: () => renderCalendar(),
                  progress: () => <TrendsTab clientId={clientId} reducedMotion={reducedMotion} />,
                  body: () => <BodyTab clientId={clientId} reducedMotion={reducedMotion} />,
                  records: () => <RecordsTab clientId={clientId} reducedMotion={reducedMotion} />,
                  surveys: () => (
                    <SurveysTab
                      clientId={clientId}
                      openOccurrenceId={surveyId}
                      reducedMotion={reducedMotion}
                      onAssignSurvey={identity === null ? null : openAssignSurvey}
                    />
                  ),
                  nutrition: () => (
                    <NutritionTab
                      clientId={clientId}
                      clientName={identity?.name ?? ''}
                      reducedMotion={reducedMotion}
                    />
                  ),
                  forma: () => (
                    <FormaTab
                      clientId={clientId}
                      onOpenClip={(next, startAt = null) => {
                        setClipStart(startAt === null ? null : { clipId: next, seconds: startAt });
                        onShowClip(next, 'push');
                      }}
                      openClipId={clipId}
                      reducedMotion={reducedMotion}
                    />
                  ),
                } satisfies Record<ClientDetailTab, () => ReactNode>
              )[tab]()}
            </TabMetaProvider>
          </CardAnnouncerProvider>
        </motion.div>
      </div>

      {/* The overlay sits over the BODY row and not over the two bars above it — the canon's own
        * inset, and it is what keeps `‹ Klienci` lit while a session is open.
        *
        * A sibling of `.body` rather than a child of it, because `.body` is what carries `inert`:
        * an overlay inside the box it makes inert would be inert itself. This screen owns WHERE the
        * overlay sits on the page; the node owns the scrim and the panel inside that box. */}
      {sessionOverlay === null ? null : (
        <div className={styles.overlayHost}>{sessionOverlay.node}</div>
      )}
      {clipId === null ? null : (
        <ClipPlayer
          clientId={clientId}
          clipId={clipId}
          onActivity={refreshTrainedExercises}
          onClose={() => {
            setClipStart(null);
            onShowClip(null, 'replace');
          }}
          onSelectClip={(next) => {
            onShowClip(next, 'replace');
          }}
          startAt={clipStart?.clipId === clipId ? clipStart.seconds : null}
        />
      )}
    </main>
  );
}

/**
 * One arrow of the `‹ Poprzednia / Następna ›` pair.
 *
 * ── A refused arrow stays FOCUSABLE ───────────────────────────────────────────────────────────
 * `aria-disabled` rather than the `disabled` attribute, because the reason this control is refused
 * is attached to the control — and a `disabled` button leaves the tab order, taking its own
 * explanation with it (`CLAUDE.md`, accessibility). The press is refused in the handler instead.
 *
 * ── The visible word is the START of the accessible name, never replaced by it ────────────────
 * "Poprzednia" is what the canon draws and what a person says; the client it leads to is appended
 * as an sr-only tail. An `aria-label` carrying the name would REPLACE the visible label and fail
 * SC 2.5.3 — the same rule the roster's row link is written against.
 */
function WalkStep({
  step,
  label,
  reason,
  reasonId,
  onOpen,
}: {
  readonly step: ClientNeighbour | null;
  readonly label: string;
  readonly reason: string;
  readonly reasonId: string;
  readonly onOpen: (clientId: number) => void;
}) {
  const { t } = useTranslation();
  const refused = step === null;
  return (
    <>
      <button
        type="button"
        className={styles.walkStep}
        aria-disabled={refused}
        aria-describedby={refused ? reasonId : undefined}
        onClick={() => {
          if (step !== null) onOpen(step.clientId);
        }}
      >
        {label}
        {step === null ? null : (
          <span className={hidden.srOnly}>
            {' '}
            {t('web:clientDetail.nav.goesTo', { name: step.name })}
          </span>
        )}
      </button>
      {refused ? (
        <span className={hidden.srOnly} id={reasonId}>
          {reason}
        </span>
      ) : null}
    </>
  );
}
